import React, { useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PlusIcon, Eye, Edit, Trash2 } from "lucide-react";
import { Button } from "@shared/ui/button";
import { useToast } from "@shared/hooks/use-toast";
import {
  FieldTooltip,
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { PageHeader } from "@shared/components/PageHeader";
import Segmentcreate from "../create/create";
import SegmentDelete from "../delete/delete";
import { useGetAllSegments, useGetAllFilterFields } from "../../../hooks";
import SegmentDetails from "../details/details";
import { HoverRevealText } from "@shared/components/HoverRevealText";
import { SegmentFilter } from "../filter/filter";
import { useGetChoices } from "@features/ux/components/shared/hooks";

interface SegmentData {
  id: string;
  name: string;
  description: string;
  audience: string;
  criteriaCount: number;
  createdBy: string;
  lastUpdated: string;
}

const SegmentLanding: React.FC = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openDetailSegment, setOpenDetailSegment] = useState(false);
  const [openDeleteSegment, setOpenDeleteSegment] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState<SegmentData | null>(
    null
  );

  // Read all params from URL
  const searchQuery = decodeURIComponent(searchParams.get("search") || "");
  const audienceType = searchParams.get("audienceType") || "";
  const criteriaCount = searchParams.get("criteriaCount") || "";
  const currentPage = parseInt(searchParams.get("page") || "1");

  const { data: segments } = useGetAllSegments(
    currentPage,
    PAGE_SIZE,
    searchQuery,
    audienceType,
    criteriaCount
  );

  const { data: getAudienceType } = useGetChoices("audience_type");
  const { data: filterData } = useGetAllFilterFields();

  const mapChoices = (data: any) =>
    data?.result?.map((item: any) => ({
      value: item.key,
      label: item.value,
    })) || [];

  const criteriaChoices = (data: any) =>
    data?.map((item: any) => ({
      value: item.category,
      label: item.categoryDisplayName,
    })) || [];

  const audienceTypeList = useMemo(
    () => mapChoices(getAudienceType),
    [getAudienceType]
  );

  const criteriaCountList = useMemo(() => criteriaChoices(filterData), [filterData]);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  const CreateSegmentButton = () => {
    const handleCreateSegment = () => {
      navigate("/communication-hub/settings/segment/create");
    };

    return (
      <Button onClick={handleCreateSegment}>
        <PlusIcon className="h-4 w-4 mr-2" />
        Add Segment
      </Button>
    );
  };

  // Update pagination
  React.useEffect(() => {
    const totalItems = segments?.count || 0;
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);
    const startItem = (currentPage - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

    setPagination({
      currentPage: currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    });
  }, [segments, currentPage]);

  const resetFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };

    delete newParams.audienceType;
    delete newParams.criteriaCount;
    delete newParams.search;

    setSearchParams(newParams);
  };

  const handleSearch = (searchTerm: string) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      ...(audienceType && { audienceType }),
      ...(criteriaCount && { criteriaCount }),
    };

    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    } else {
      delete newParams.search;
    }

    newParams.page = "1";
    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      page: page.toString(),
    };
    setSearchParams(newParams);
  };

  const columns: TableColumn<SegmentData>[] = [
    {
      key: "name",
      header: "Name",
      sortable: true,
      render: (value: string, row: SegmentData) => (
        <div>
          <p className="font-medium">{value}</p>
          <HoverRevealText text={row.description} />
        </div>
      ),
    },
    {
      key: "audienceTypeName",
      header: "Audience",
      sortable: true,
    },
    {
      key: "criteriaCount",
      header: "Criteria Count",
      sortable: true,
      render: (value: number) => <span>{value} condition(s)</span>,
    },
    {
      key: "createdDate",
      header: "Created On",
      render: (value, row: any) => (
        <div>
          <p className="font-medium">{value ?? "NA"}</p>
          <p className="text-xs text-muted-foreground">
            {row.createdBy ?? "NA"}
          </p>
        </div>
      ),
    },
    {
      key: "lastModifiedDate",
      header: "Updated On",
      render: (value, row: any) => (
        <div>
          <p className="font-medium">{value ?? "NA"}</p>
          <p className="text-xs text-muted-foreground">
            {row.updatedBy ?? "NA"}
          </p>
        </div>
      ),
    },
  ];

  const actions = [
    {
      label: "View",
      onClick: (row: SegmentData) => {
        setOpenDetailSegment(true);
        setSelectedSegment(row);
      },
      icon: <Eye className="h-4 w-4 mr-2" />,
    },
    {
      label: "Edit",
      onClick: (row: SegmentData) => {
        navigate(`/communication-hub/settings/segment/edit/${row.id}`);
      },
      icon: <Edit className="h-4 w-4 mr-2" />,
    },
    {
      label: "Delete",
      onClick: (row: SegmentData) => {
        setOpenDeleteSegment(true);
        setSelectedSegment(row);
      },
      icon: <Trash2 className="h-4 w-4 mr-2" />,
    },
  ];

  const filterPanel = (
    <SegmentFilter
      audienceType={audienceType}
      criteriaCount={criteriaCount}
      audienceTypeList={audienceTypeList}
      criteriaCountList={criteriaCountList}
      onApplyFilters={(filters) => {
        const newParams: any = {
          ...Object.fromEntries(searchParams.entries()),
          ...(filters.audienceType && { audienceType: filters.audienceType }),
          ...(filters.criteriaCount && {
            criteriaCount: filters.criteriaCount,
          }),
        };
        newParams.page = "1";
        setSearchParams(newParams);
      }}
      onResetFilters={resetFilters}
    />
  );

  const hasActiveFilters = audienceType || criteriaCount;

  return (
    <div className="space-y-6 p-1">
      {/* Header Card */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex justify-between items-center">
          <div>
            <PageHeader
              title="Audience Segments"
              subtitle="Create and manage reusable audience segments"
            />
          </div>
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <CreateSegmentButton />
            </TooltipTrigger>
            <FieldTooltip>
              Create a new segment to define specific audience groups
            </FieldTooltip>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="space-y-4">
        <GenericTable
          data={segments?.results || []}
          columns={columns}
          rowKey="id"
          selectable={false}
          actions={actions}
          actionsType="dropdown"
          emptyMessage="No segments found matching your criteria"
          pagination={pagination}
          onPageChange={handlePageChange}
          onSearch={handleSearch}
          searchPlaceholder="Search by segment name..."
          filterPanel={filterPanel}
          filters={
            hasActiveFilters ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {[!!audienceType, !!criteriaCount].filter(Boolean).length}{" "}
                  filter(s) active
                </span>
              </div>
            ) : null
          }
        />
      </div>
      <SegmentDetails
        openDetailSegment={openDetailSegment}
        setOpenDetailSegment={setOpenDetailSegment}
        selectedSegment={selectedSegment}
      />
      <SegmentDelete
        openDeleteSegment={openDeleteSegment}
        setOpenDeleteSegment={setOpenDeleteSegment}
        selectedSegment={selectedSegment}
      />
    </div>
  );
};

export default SegmentLanding;
